import { AlertCircle, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import { isValidElement } from "react";
import { toast as sonnerToast } from "sonner";
import type { ExternalToast, ToastClassnames, ToasterProps } from "sonner";

import { cn } from "@/shadcn/lib/utils";

type SonnerToast = typeof sonnerToast;
type ToastMessage = Parameters<SonnerToast["message"]>[0];
type ToastOptions = ExternalToast | undefined;
type ToastIcons = NonNullable<ToasterProps["icons"]>;

type NotificationVariant = "success" | "info" | "warn" | "error";

const BASE_TOAST_CLASS =
  "group toast rounded-lg border shadow-subtle group-[.toaster]:text-foreground";
const AGELESS_SUCCESS_TOAST_SUPPRESSED_PATHS = ["/ageless/onboarding", "/ageless/catalog"];

export const baseToastOptions: ToastOptions = {
  duration: 4000,
  classNames: {
    toast: `${BASE_TOAST_CLASS} group-[.toaster]:border-border group-[.toaster]:bg-white`,
    title: "type-label-md font-medium text-foreground",
    description: "type-body-xs text-muted-foreground",
    icon: "mt-0.5",
    actionButton:
      "group-[.toast]:rounded-md group-[.toast]:bg-secondary group-[.toast]:px-3 group-[.toast]:text-foreground group-[.toast]:hover:bg-secondary/80",
    cancelButton:
      "group-[.toast]:rounded-md group-[.toast]:border group-[.toast]:border-border group-[.toast]:bg-transparent group-[.toast]:px-3 group-[.toast]:text-foreground/80",
  },
};

const VARIANT_OPTIONS: Record<NotificationVariant, ToastOptions> = {
  success: {
    classNames: {
      toast: "group-[.toaster]:border-success/30 group-[.toaster]:bg-white",
      icon: "text-success",
    },
  },
  info: {
    classNames: {
      toast: "group-[.toaster]:border-info/30 group-[.toaster]:bg-white",
      icon: "text-info",
    },
  },
  warn: {
    classNames: {
      toast: "group-[.toaster]:border-warning/40 group-[.toaster]:bg-white",
      icon: "text-warning",
    },
  },
  error: {
    classNames: {
      toast: "group-[.toaster]:border-destructive/40 group-[.toaster]:bg-white",
      icon: "text-destructive",
    },
  },
};

export const toastIcons: ToastIcons = {
  info: <Info className="h-4 w-4 text-info" />,
  success: <CheckCircle2 className="h-4 w-4 text-success" />,
  warning: <AlertTriangle className="h-4 w-4 text-warning" />,
  error: <AlertCircle className="h-4 w-4 text-destructive" />,
};

export const mergeToastOptions = (
  defaultOptions?: ToastOptions,
  overrideOptions?: ToastOptions,
): ToastOptions => {
  if (!defaultOptions && !overrideOptions) {
    return undefined;
  }

  if (!defaultOptions) {
    return overrideOptions;
  }

  if (!overrideOptions) {
    return defaultOptions;
  }

  return {
    ...defaultOptions,
    ...overrideOptions,
    classNames: mergeClassNames(defaultOptions.classNames, overrideOptions.classNames),
  };
};

const getVariantToastOptions = (variant: NotificationVariant, options?: ToastOptions) =>
  mergeToastOptions(mergeToastOptions(baseToastOptions, VARIANT_OPTIONS[variant]), options);

const baseSuccess = sonnerToast.success.bind(sonnerToast);
const baseError = sonnerToast.error.bind(sonnerToast);
const baseInfo = sonnerToast.info.bind(sonnerToast);
const baseWarning = sonnerToast.warning.bind(sonnerToast);

const UNRENDERABLE_MESSAGE_FALLBACK = "Something went wrong. Please try again.";

// Sonner renders the message as a React child. A plain object — typically a ProblemDetails body
// passed straight through from `error.response.data` — throws React #31 and blanks the page, so
// it is swapped for generic copy here rather than trusted. Elements, strings, numbers, arrays and
// render functions are all valid children and pass through untouched.
const renderable = (message: ToastMessage): ToastMessage => {
  if (
    typeof message === "object" &&
    message !== null &&
    !isValidElement(message) &&
    !(Symbol.iterator in message)
  ) {
    return UNRENDERABLE_MESSAGE_FALLBACK;
  }
  return message;
};

export const toast = {
  success: (message: ToastMessage, options?: ToastOptions) => {
    if (shouldSuppressAgelessSuccessToast()) {
      return undefined;
    }

    return baseSuccess(renderable(message), getVariantToastOptions("success", options));
  },
  info: (message: ToastMessage, options?: ToastOptions) =>
    baseInfo(renderable(message), getVariantToastOptions("info", options)),
  warn: (message: ToastMessage, options?: ToastOptions) =>
    baseWarning(renderable(message), getVariantToastOptions("warn", options)),
  error: (message: ToastMessage, options?: ToastOptions) =>
    baseError(renderable(message), getVariantToastOptions("error", options)),
};

function mergeClassNames(
  defaultClassNames?: ToastClassnames,
  overrideClassNames?: ToastClassnames,
): ToastClassnames | undefined {
  if (!defaultClassNames && !overrideClassNames) {
    return undefined;
  }

  if (!defaultClassNames) {
    return overrideClassNames;
  }

  if (!overrideClassNames) {
    return defaultClassNames;
  }

  const merged: ToastClassnames = { ...defaultClassNames };
  const keys = Object.keys(overrideClassNames) as Array<keyof ToastClassnames>;

  keys.forEach((key) => {
    const value = overrideClassNames[key];

    if (value === undefined || value === "") {
      return;
    }

    const currentValue = merged[key];
    merged[key] = currentValue ? cn(currentValue, value) : value;
  });

  return merged;
}

function shouldSuppressAgelessSuccessToast(): boolean {
  if (typeof globalThis.location === "undefined") {
    return false;
  }

  return AGELESS_SUCCESS_TOAST_SUPPRESSED_PATHS.some((path) =>
    globalThis.location.pathname.includes(path),
  );
}
